import { EMPTY_PROFILE, MAX_EVENTS_PER_BATCH, type ActivityEvent, type ActivityTarget } from '@guildite/domain/activity';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import { useCurrentUser, useData } from '../../data/DataContext.tsx';
import type { ActivityProfileView } from '../../data/ports.ts';
import { useWhereabouts } from '../location/LocationContext.tsx';

// What you do inside Guildite (views with time, searches, taps, likes,
// saves, shares, follows, add to cart, purchases, guild messages,
// notifications opened), sent to the Worker in small batches for your own
// "For you" feed, recently viewed, search and sellers' totals. Nothing from
// outside the app; nothing at all when Personalisation is off or signed out.
export type TrackInput = Omit<ActivityEvent, 'at'>;

type ActivityState = {
  personalise: boolean;
  profile: ActivityProfileView;
  track(event: TrackInput): void;
  setPersonalise(on: boolean): Promise<void>;
  clear(): Promise<void>;
};

export const PERSONALISE_KEY = 'guildite.personalise';
const FLUSH_MS = 15_000;

function storedOff(): boolean {
  try {
    return localStorage.getItem(PERSONALISE_KEY) === 'off';
  } catch {
    return false;
  }
}

const EMPTY: ActivityProfileView = { ...EMPTY_PROFILE, personalise: true };
const ActivityContext = createContext<ActivityState | null>(null);

export function ActivityProvider({ children }: { children: ReactNode }) {
  const { activity } = useData();
  const user = useCurrentUser();
  const { here } = useWhereabouts();
  const [profileState, setProfile] = useState<{ uid: string; profile: ActivityProfileView } | null>(null);
  const [offHere, setOffHere] = useState(storedOff);
  const queue = useRef<ActivityEvent[]>([]);
  const town = useRef(here);
  useEffect(() => { town.current = here; }, [here]);
  const uid = user?.uid ?? null;
  const profile = uid && profileState?.uid === uid ? profileState.profile : EMPTY;
  const personalise = !offHere && profile.personalise;

  useEffect(() => {
    if (!uid) return;
    let active = true;
    activity.mine().then(found => {
      if (!active) return;
      setProfile({ uid, profile: found });
      setOffHere(!found.personalise);
      try {
        if (found.personalise) localStorage.removeItem(PERSONALISE_KEY);
        else localStorage.setItem(PERSONALISE_KEY, 'off');
      } catch { /* Not remembered on this device. */ }
    }, () => undefined);
    return () => { active = false; };
  }, [activity, uid]);

  const flush = useCallback(() => {
    if (!queue.current.length) return;
    const events = queue.current.splice(0, MAX_EVENTS_PER_BATCH);
    activity.record({ batchId: crypto.randomUUID(), events, town: town.current }).catch(() => undefined);
  }, [activity]);

  useEffect(() => {
    if (!uid || !personalise) {
      queue.current = [];
      return;
    }
    const timer = window.setInterval(flush, FLUSH_MS);
    const hidden = () => { if (document.visibilityState === 'hidden') flush(); };
    document.addEventListener('visibilitychange', hidden);
    window.addEventListener('pagehide', flush);
    return () => {
      flush();
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', hidden);
      window.removeEventListener('pagehide', flush);
    };
  }, [uid, personalise, flush]);

  const track = useCallback((event: TrackInput) => {
    if (!uid || !personalise) return;
    queue.current.push({ ...event, at: new Date().toISOString() });
    if (queue.current.length >= MAX_EVENTS_PER_BATCH) flush();
  }, [uid, personalise, flush]);

  const setPersonalise = useCallback(async (on: boolean) => {
    await activity.setPersonalise(on);
    queue.current = [];
    setOffHere(!on);
    try {
      if (on) localStorage.removeItem(PERSONALISE_KEY);
      else localStorage.setItem(PERSONALISE_KEY, 'off');
    } catch { /* Not remembered on this device. */ }
    if (uid) setProfile({ uid, profile: on ? { ...profile, personalise: true } : { ...EMPTY, personalise: false } });
  }, [activity, uid, profile]);

  const clear = useCallback(async () => {
    await activity.clear();
    queue.current = [];
    if (uid) setProfile({ uid, profile: { ...EMPTY, personalise } });
  }, [activity, uid, personalise]);

  const value = useMemo(() => ({ personalise, profile, track, setPersonalise, clear }), [personalise, profile, track, setPersonalise, clear]);
  return <ActivityContext.Provider value={value}>{children}</ActivityContext.Provider>;
}

const NONE: ActivityState = { personalise: false, profile: EMPTY, track: () => undefined, setPersonalise: async () => undefined, clear: async () => undefined };

export function useActivity(): ActivityState {
  return useContext(ActivityContext) ?? NONE;
}

// Records a view with the seconds it was on screen (time while the app is in
// the background doesn't count), when the page closes or the target changes.
export function useViewTime(target: ActivityTarget | null) {
  const { track } = useActivity();
  const key = target ? `${target.kind}:${target.id}` : '';
  useEffect(() => {
    if (!key) return;
    const [kind, ...rest] = key.split(':');
    const viewed = { kind, id: rest.join(':') } as ActivityTarget;
    let seconds = 0;
    let since = document.visibilityState === 'visible' ? Date.now() : null;
    const visibility = () => {
      if (document.visibilityState === 'hidden' && since !== null) { seconds += (Date.now() - since) / 1000; since = null; }
      if (document.visibilityState === 'visible' && since === null) since = Date.now();
    };
    document.addEventListener('visibilitychange', visibility);
    return () => {
      document.removeEventListener('visibilitychange', visibility);
      if (since !== null) seconds += (Date.now() - since) / 1000;
      track({ type: 'view', target: viewed, seconds: Math.round(seconds) });
    };
  }, [key, track]);
}
